// Copyright - Kyley Harris (2021) - All rights to copy and reuse allowed
// a version based approach to upgrading a database

export interface GetOptions {
  indexName?: string;
  query?: IDBValidKey | IDBKeyRange | null;
  direction?: IDBCursorDirection;
}

const debug = false;
export const log = (s: string) => {
  if (debug) console.debug(s);
};

export interface DatabaseUpgrade {
  version: number;
  execute: (db: IDBDatabase, tran: IDBTransaction) => void;
}

// db connection plan including any and all upgrades
export interface DatabaseOptions {
  dbName: string;
  version: number;
  upgrades?: DatabaseUpgrade[];
  onBlocked: () => void;
}

export interface CreateIndexOptions {
  name: string;
  keyPath: string | string[];
  options: IDBIndexParameters;
  delete?: boolean;
}

export interface CreateObjectStoreOptions {
  name: string;
  indexes?: CreateIndexOptions[];
}

/** ----------------------------- Small helpers ------------------------------ */

function requestToPromise<T = unknown>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result as T);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request error'));
  });
}

function transactionDone(tr: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    tr.oncomplete = () => resolve();
    tr.onerror = () => reject(tr.error ?? new Error('IndexedDB transaction error'));
    tr.onabort = () => reject(tr.error ?? new Error('IndexedDB transaction aborted'));
  });
}

/** ------------------------- Schema helpers (migrations) -------------------- */

// may only be called from inside a migration path
export function newDbItemObjectStore(db: IDBDatabase, options: CreateObjectStoreOptions): IDBObjectStore {
  const store = db.createObjectStore(options.name, { keyPath: 'id', autoIncrement: false });
  options.indexes?.forEach(option => {
    if (!option.delete) {
      // guard against duplicate index creation in reentrant upgrades
      if (!store.indexNames.contains(option.name)) {
        store.createIndex(option.name, option.keyPath, option.options);
      }
    }
  });
  return store;
}

// may only be called as part of a migration path
export function updateDbItemObjectStore(tran: IDBTransaction, options: CreateObjectStoreOptions): IDBObjectStore {
  const store = tran.objectStore(options.name);
  options.indexes?.forEach(option => {
    if (!option.delete) {
      if (!store.indexNames.contains(option.name)) {
        store.createIndex(option.name, option.keyPath, option.options);
      }
    } else if (store.indexNames.contains(option.name)) {
      store.deleteIndex(option.name);
    }
  });
  return store;
}

/** ---------------------------- Open / Close DB ----------------------------- */

// promise based db open that manages the upgrade path
export function openDb(options: DatabaseOptions): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = window.indexedDB.open(options.dbName, options.version);

    request.onerror = () => {
      log(`Database Connection Error ${options.dbName}`);
      reject(request.error ?? new Error('Failed to open IndexedDB'));
    };

    request.onblocked = () => {
      log(`Database Pending until unblocked ${options.dbName}`);
      options.onBlocked && options.onBlocked();
    };

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const openReq = event.target as IDBOpenDBRequest;
      const db = openReq.result;
      const tran = openReq.transaction!;
      const oldVersion = event.oldVersion;

      // Ensure deterministic, ordered upgrades
      const upgrades = (options.upgrades || [])
        .filter(u => u.version > oldVersion)
        .sort((a, b) => a.version - b.version);

      try {
        for (const u of upgrades) {
          u.execute(db, tran);
        }
      } catch (e) {
        // Throwing inside onupgradeneeded will abort the versionchange transaction
        log(`Upgrade error on ${options.dbName}: ${(e as Error).message}`);
        throw e;
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      log(`Database Connection Open ${options.dbName}`);

      // If another tab attempts to upgrade, close gracefully to unblock it
      db.onversionchange = () => {
        log(`Database version change detected, closing ${options.dbName}`);
        try {
          db.close();
        } catch {
          /* noop */
        }
      };

      db.onclose = () => log(`Database Connection Closed ${options.dbName}`);
      resolve(db);
    };
  });
}

export function closeDb(conn: IDBDatabase): Promise<void> {
  return new Promise<void>(resolve => {
    try {
      conn.close();
    } finally {
      resolve();
    }
  });
}

/** ------------------------------- Queries ---------------------------------- */

export function fetchCursor<T>(
  objectStoreOrIndex: IDBObjectStore | IDBIndex,
  options?: { query?: IDBValidKey | IDBKeyRange | null; direction?: IDBCursorDirection }
): Promise<T[]> {
  return new Promise<T[]>((resolve, reject) => {
    const results: T[] = [];
    const req = objectStoreOrIndex.openCursor(options?.query ?? null, options?.direction);

    req.onsuccess = event => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        results.push(cursor.value as T);
        cursor.continue();
      } else {
        resolve(results);
      }
    };
    req.onerror = () => reject(req.error ?? new Error('Cursor request failed'));
  });
}

export function deleteAll(conn: IDBDatabase, storeName: string): Promise<void> {
  const tran = conn.transaction(storeName, 'readwrite');
  const store = tran.objectStore(storeName);
  store.clear();
  return transactionDone(tran).then(() => {
    log(`Cleared all from ${storeName}`);
  });
}

export async function get<T>(conn: IDBDatabase, storeName: string, options?: GetOptions): Promise<T | null> {
  const tran = conn.transaction(storeName, 'readonly');
  const store = tran.objectStore(storeName);
  const source: IDBObjectStore | IDBIndex = options?.indexName ? store.index(options.indexName) : store;

  // Collect during transaction
  const result = await fetchCursor<T>(source, { query: options?.query ?? null, direction: options?.direction });

  // Resolve after transaction is complete to surface errors cleanly
  await transactionDone(tran);
  log(`transaction complete on ${storeName}`);
  return result.length > 0 ? result[0] : null;
}

export function put<T extends { id?: IDBValidKey }>(
  conn: IDBDatabase,
  storeName: string,
  item: T
): Promise<IDBValidKey> {
  const tran = conn.transaction(storeName, 'readwrite');
  const store = tran.objectStore(storeName);
  const req = store.put(item);
  return Promise.all([requestToPromise<IDBValidKey>(req), transactionDone(tran)]).then(([key]) => {
    log(`transaction complete on ${storeName}`);
    return key;
  });
}

export function putAll<T>(conn: IDBDatabase, storeName: string, items: T[]): Promise<void> {
  const tran = conn.transaction(storeName, 'readwrite');
  const store = tran.objectStore(storeName);

  return new Promise<void>((resolve, reject) => {
    let i = 0;

    const next = () => {
      if (i >= items.length) return;
      const req = store.put(items[i++]);
      req.onsuccess = next;
      req.onerror = () => reject(req.error ?? new Error('putAll failed'));
    };

    tran.oncomplete = () => resolve();
    tran.onerror = () => reject(tran.error ?? new Error('putAll transaction error'));
    tran.onabort = () => reject(tran.error ?? new Error('putAll transaction aborted'));

    next();
  });
}

export async function getAll<T>(conn: IDBDatabase, storeName: string, options?: GetOptions): Promise<T[]> {
  const tran = conn.transaction(storeName, 'readonly');
  const store = tran.objectStore(storeName);
  const source: IDBObjectStore | IDBIndex = options?.indexName ? store.index(options.indexName) : store;

  // Use cursor path for broad compatibility (Safari et al.)
  const result = await fetchCursor<T>(source, { query: options?.query ?? null, direction: options?.direction });

  await transactionDone(tran);
  log(`transaction complete on ${storeName}`);
  return result;
}

export function del(conn: IDBDatabase, storeName: string, id: IDBValidKey): Promise<void> {
  const tran = conn.transaction(storeName, 'readwrite');
  const store = tran.objectStore(storeName);
  const req = store.delete(id);

  return Promise.all([requestToPromise(req), transactionDone(tran)]).then(() => {
    log(`transaction complete on ${storeName}`);
  });
}
